import moment from "moment";
import { CalendarEvent, google, office365, outlook } from "calendar-link";
import PublicApi from "./public-api";
import Field from "./field";
import EmailRenderUtils from "./email-render-utils";
import Email from "./email";
import DocumentValue from "./document-value";
import { getEntryParamsString, parsePathId } from "../utils";
import IField from "../interface/field";
import IDocumentValue from "../interface/document-value";
import IDocument from "../interface/document";
import Global from "../global";
import EmailEventCalendar from "../enum/email-event-calendar";

class EmailEventSingleton {

    /* Public Methods */

    public async getEventUrl(source: string, entityId: number, doc: IDocument): Promise<string> {
        const param: string[] = getEntryParamsString(source);
        const start: string = await this.getStart(doc, param[2]);
        const ics: boolean = param[0] === EmailEventCalendar.iOS;
        const event: CalendarEvent = {
            start,
            title: await this.replaceSubstitutions(param[1], entityId, doc, ics),
            duration: [await this.getDuration(doc, param[3], param[4], start), 'minute'],
            description: await this.replaceSubstitutions(param[5], entityId, doc, ics)
        }
        switch (param[0]) {
            case EmailEventCalendar.Google:
                return google(event);
            case EmailEventCalendar.iOS:
                return PublicApi.getIcsAddress(event);
            case EmailEventCalendar.Outlook:
                return outlook(event);
            case EmailEventCalendar.Office:
                return office365(event);
        }
        return source;
    }

    /* Private Methods */

    private async getStart(doc: IDocument, pathId: string): Promise<string> {
        return await this.getDate(doc, pathId);
    }

    private async getDuration(doc: IDocument, toPathId: string, durPathId: string, startIso: string): Promise<number> {
        if (toPathId) {
            const from: moment.Moment = moment(startIso, moment.ISO_8601);
            const to: moment.Moment = moment(await this.getDate(doc, toPathId), moment.ISO_8601);
            if (from.isValid() && to.isValid()) {
                const diff: number = to.diff(from, 'minutes');
                if (diff > 0) {
                    return diff;
                }
            }
        } else if (durPathId) {
            const path: number[] = parsePathId(durPathId);
            const value: IDocumentValue = await DocumentValue.getByPath(doc, path);
            if (typeof value === 'number') {
                return value;
            }
        }
        return Global.DEFAULT_DURATION;
    }

    private async replaceSubstitutions(source: string, entityId: number, doc: IDocument, ics: boolean = false): Promise<string> {
        const decoded: string = EmailRenderUtils.decodeSubstitutions(source);
        const result: string = await Email.replaceSubstitutions(decoded, entityId, doc, !ics);
        if (ics) {
            return EmailRenderUtils.unrenderLinebreaks(result);
        }
        return result;
    }

    private async getDate(doc: IDocument, pathId: string): Promise<string> {
        const path: number[] = parsePathId(pathId);
        const field: IField | null = Field.get(path[path.length - 1]);
        if (field) {
            const value: IDocumentValue = await DocumentValue.getByPath(doc, path);
            const unix: number | null = DocumentValue.getDatetime(value, field);
            if (unix) {
                return value as string;
            }
        }
        return (new Date()).toISOString();
    }

}

const EmailEvent = new EmailEventSingleton();

export default EmailEvent;